import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import { isLocalDev } from 'utils/environment';
import HelpService from 'service/helpService';
import useAbortController from 'hooks/useAbortController';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function HelpPanel({ open, onClose }) {
  const [signal] = useAbortController();
  const [articles, setArticles] = useState({});
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [articleContent, setArticleContent] = useState('');
  const [contentLoading, setContentLoading] = useState(false);

  // Fetch all articles when panel opens
  useEffect(() => {
    if (open) {
      fetchArticles();
    }
  }, [open]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await HelpService.getAllArticles(signal);
      if (response?.code === 0 && response?.data) {
        setArticles(response.data);
        setSearchResults(null);
        setSelectedArticle(null);
        setSearchQuery('');
      }
    } catch (error) {
      if (isLocalDev) console.error('Error fetching articles:', error);
    }
    setLoading(false);
  };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await HelpService.searchArticles(query, signal);
      if (response?.code === 0 && response?.data) {
        setSearchResults(response.data);
      }
    } catch (error) {
      if (isLocalDev) console.error('Error searching articles:', error);
    }
  };

  const handleSelectArticle = async (slug) => {
    setContentLoading(true);
    try {
      const response = await HelpService.getArticleBySlug(slug, signal);
      if (response?.code === 0 && response?.data) {
        setSelectedArticle(response.data);
        setArticleContent(response.data.content || '');
      }
    } catch (error) {
      if (isLocalDev) console.error('Error fetching article:', error);
    }
    setContentLoading(false);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
    setArticleContent('');
  };

  const categories = Object.keys(articles);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 500 },
          maxWidth: '90vw',
        },
        overflowY: 'auto',
        // Custom scrollbar styling for better visibility
        '&::-webkit-scrollbar': {
            width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.3)',
            borderRadius: 4,
        },
        // Enable momentum/smooth scrolling on iOS and Safari
        WebkitOverflowScrolling: 'touch',
        
        // Establish positioning context for the scroll container
        position: 'relative',                
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedArticle ? (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToList}
              sx={{ color: 'white', textTransform: 'none', justifyContent: 'flex-start' }}
            >
              Back
            </Button>
          ) : (
            <Typography variant="h6">Help & Support</Typography>
          )}
          <Button
            onClick={onClose}
            sx={{ color: 'white', minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>

        {selectedArticle ? (
          /* Article View */
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {selectedArticle.title}
            </Typography>
            <Chip
              label={selectedArticle.category}
              size="small"
              sx={{ mb: 2 }}
            />

            {contentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  '& h1': { fontSize: '1.75rem', fontWeight: 'bold', mt: 2, mb: 1 },
                  '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
                  '& h3': { fontSize: '1.25rem', fontWeight: 'bold', mt: 2, mb: 1 },
                  '& p': { mb: 1, lineHeight: 1.6 },
                  '& ul, & ol': { mb: 1, pl: 2 },
                  '& li': { mb: 0.5 },
                  '& table': { width: '100%', borderCollapse: 'collapse', mb: 1 },
                  '& th, & td': { border: '1px solid #ddd', p: 1, textAlign: 'left' },
                  '& th': { bgcolor: '#f5f5f5', fontWeight: 'bold' },
                  '& code': {
                    bgcolor: '#f5f5f5',
                    p: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                  },
                  '& pre': {
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: '4px',
                    overflow: 'auto',
                  },
                  '& blockquote': { borderLeft: '4px solid #ddd', pl: 2, color: '#666' },
                  '& strong': { fontWeight: 'bold' },
                  '& em': { fontStyle: 'italic' },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{articleContent}</ReactMarkdown>
              </Box>
            )}
          </Box>
        ) : (
          /* Articles List View */
          <>
            {/* Search Bar */}
            <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} />,
                }}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : searchResults !== null ? (
              /* Search Results */
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {searchResults.length > 0 ? (
                  <List>
                    {searchResults.map((article) => (
                      <ListItem key={article.id} disablePadding>
                        <ListItemButton onClick={() => handleSelectArticle(article.slug)}>
                          <ListItemText
                            primary={article.title}
                            secondary={article.category}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      No articles found for "{searchQuery}"
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              /* Categories and Articles */
              <>
                {categories.length > 0 && (
                  <Tabs
                    value={selectedCategory}
                    onChange={(e, newValue) => setSelectedCategory(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f9f9f9' }}
                  >
                    {categories.map((category, idx) => (
                      <Tab key={idx} label={category} />
                    ))}
                  </Tabs>
                )}

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {categories.length > 0 ? (
                    <List>
                      {articles[categories[selectedCategory]]?.map((article) => (
                        <ListItem key={article.id} disablePadding>
                          <ListItemButton onClick={() => handleSelectArticle(article.slug)}>
                            <ListItemText primary={article.title} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        No help articles available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
